/**
 * ENG-8726: read the agent's CHARTER `logging_mode` on the host.
 *
 * WHY THIS MODULE EXISTS. `logging_mode` has been declared in
 * `packages/core/src/types/charter.ts` and lint-enforced across CHARTER/TOOLS
 * since the beginning, and until this module nothing anywhere read it at
 * runtime. ENG-8573 built the enforcement — `RedactionContext.hashOnly`, which
 * withholds every target — and ENG-8575 threaded it from `maybeScanToolCalls`
 * through `scanAgentToolCalls` to the redaction context. But no caller ever set
 * it. An optional boolean with no producer is indistinguishable from an absent
 * feature: every test that passed `hashOnly: true` passed, the flag was wired
 * end to end, and on the fleet it was `undefined` at every tick. This module is
 * the producer.
 *
 * WHERE THE VALUE COMES FROM. `/host/refresh` returns the charter's
 * `raw_content` and the provisioning path writes it verbatim to
 * `<agentDir>/provision/CHARTER.md` — a drift-tracked artifact, so it is
 * rewritten whenever the control plane's copy changes. Reading the file rather
 * than the refresh response is deliberate: the scanner runs earlier in the tick
 * than the refresh, on a 10-minute throttle of its own, and threading a value
 * across that gap would make the enforcement depend on tick ordering. The file
 * is also the artefact an operator can inspect when asking "why is this agent's
 * audit row empty", which the in-memory copy is not.
 *
 * FAIL CLOSED, AND SAY SO. When the mode cannot be established — no charter on
 * disk, unparseable frontmatter, an unrecognised value — this returns `null` and
 * the caller treats it as `hash-only`. Over-disclosure is a governance breach
 * that cannot be corrected in place (the ingest is `ON CONFLICT DO NOTHING`, so
 * a plaintext path in the table stays there); under-disclosure costs audit
 * fidelity on rows that still carry `tool_name`. The costlier error picks the
 * direction.
 *
 * That choice has a real cost and it is the reason `reason` exists: an all-
 * `none` table looks identical whether the agent genuinely declared `hash-only`
 * or the host simply could not read its charter. Nothing on the wire
 * distinguishes them — deliberately, since a host-asserted "I could not read my
 * own governance record" is not evidence the control plane should store. The log
 * line is the only discriminator, so it has to name the reason. See
 * `docs/runbooks/tool-call-audit-rollout.md`.
 */

import { readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

import { extractFrontmatter } from '@augmented/core';

import { agentRuntimeKey } from './agent-runtime-key.js';

export type AgentLoggingMode = 'hash-only' | 'redacted' | 'full-local';

/** The closed set from `CharterFrontmatter.logging_mode`. */
const LOGGING_MODES: readonly AgentLoggingMode[] = ['hash-only', 'redacted', 'full-local'];

export type LoggingModeUnknownReason =
  /** No CHARTER.md at the provisioned path (or it could not be read). */
  | 'no-charter'
  /** The file exists but has no parseable YAML frontmatter. */
  | 'unparseable'
  /** Frontmatter parsed, but carries no `logging_mode`. */
  | 'not-declared'
  /** `logging_mode` is present and is not one of the three known values. */
  | 'unrecognised';

export interface LoggingModeReading {
  /** The declared mode, or null when it could not be established. */
  mode: AgentLoggingMode | null;
  /** Why `mode` is null. Absent when the mode was read successfully. */
  reason?: LoggingModeUnknownReason;
}

/**
 * Where the provisioned charter lives.
 *
 * Resolved through `agentRuntimeKey` rather than the codename, mirroring
 * `pathSaltPath` exactly. The three host-local inputs the scanner depends on —
 * the salt, the cursor state and now the charter — must resolve under one key,
 * or a test that seams `homeDir` would read one agent's cursor and another's
 * charter. (The core `getAgentDir` seam resolves the same path via the ADR-0049
 * symlink, but takes no `homeDir`, and a reader that quietly ignored the seam
 * every other input honours is its own silent bug.)
 */
export function charterPath(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env['HOME']?.trim() || homedir());
  const key = agentRuntimeKey(codeName, homeDir);
  // agent-dir-allow: layout-aware via agentRuntimeKey, mirrors pathSaltPath (ADR-0049 slice 5)
  return join(home, '.augmented', key, 'provision', 'CHARTER.md');
}

/**
 * Read the agent's declared `logging_mode` from its provisioned CHARTER.md.
 *
 * Never throws — this runs inside the manager tick behind a `void` call.
 */
export function readAgentLoggingMode(codeName: string, homeDir?: string): LoggingModeReading {
  let raw: string;
  try {
    raw = readFileSync(charterPath(codeName, homeDir), 'utf-8');
  } catch {
    return { mode: null, reason: 'no-charter' };
  }

  let frontmatter: Record<string, unknown> | null;
  try {
    frontmatter = extractFrontmatter(raw).frontmatter as Record<string, unknown> | null;
  } catch {
    // extractFrontmatter reports parse failures through its `error` field rather
    // than by throwing, but it is fed an arbitrary file from disk and a throw
    // here would escape into the poll cycle.
    return { mode: null, reason: 'unparseable' };
  }
  // DO NOT CARRY `parsed.error` INTO THE READING, however tempting. Four of
  // extractFrontmatter's five failure messages are fixed literals and would be
  // safe, but the fifth is `YAML parse error: ${e.message}` — and the `yaml`
  // library quotes the offending source line back at you:
  //
  //   Nested mappings are not allowed in compact mappings at line 2, column 16:
  //
  //   secret_client: acme-legal-redundancy
  //                  ^
  //
  // That is charter frontmatter in `manager.log`, written for the agent whose
  // charter says record nothing about what it touches — the precise disclosure
  // this module exists to prevent, arriving through the diagnostic added to
  // explain it. The reason set stays closed and content-free.
  //
  // The information genuinely lost is "unterminated `---`" vs "invalid YAML",
  // which does not change what the operator does next: both mean go and look at
  // the charter on the host. The distinctions that DO change the response —
  // no-charter (provisioning), not-declared (generator bug), unrecognised
  // (version skew) — are already separate reasons. (CodeRabbit suggested
  // carrying the detail; declined for the above.)
  if (!frontmatter) return { mode: null, reason: 'unparseable' };

  const declared = frontmatter['logging_mode'];
  if (declared === undefined || declared === null) return { mode: null, reason: 'not-declared' };
  if (typeof declared !== 'string' || !LOGGING_MODES.includes(declared as AgentLoggingMode)) {
    // A value outside the union is NOT read as "some other mode, so disclose".
    // It is a charter this host does not understand, and the safe reading of a
    // governance record you cannot parse is the strictest one it could mean.
    return { mode: null, reason: 'unrecognised' };
  }
  return { mode: declared as AgentLoggingMode };
}

/**
 * Does this agent's charter permit disclosing a redacted target at all?
 *
 * The single place the fail-closed direction is expressed. `redacted` and
 * `full-local` disclose (the redaction rules still apply — this is not a
 * bypass); `hash-only` and every unknown do not.
 */
export function loggingModeWithholdsTargets(reading: LoggingModeReading): boolean {
  return reading.mode === null || reading.mode === 'hash-only';
}
